import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Check, Star, ChevronDown, ChevronUp, Menu, X, UserCircle } from 'lucide-react';

const Home = () => {
  const [openFaq, setOpenFaq] = React.useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const faqs = [
    {
      question: "How does Big Clipboard work?",
      answer: "Big Clipboard allows you to save and organize text, links, images, and other content across all your devices. Simply copy content and it's automatically saved to your personal clipboard in the cloud."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, we use industry-standard encryption to protect your data. Your clipboard items are private by default and only you can access them unless you choose to make them public."
    },
    {
      question: "Can I share clipboard items with others?",
      answer: "Absolutely! You can make any item public and share the link with others. Public items can be viewed, voted on, and commented on by the community."
    },
    {
      question: "What platforms are supported?",
      answer: "Big Clipboard works on Windows, Mac, Linux, Android, iPhone, and all major browsers including Chrome, Firefox, Safari, and Edge."
    }
  ];

  return (
    <div className="home-page">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/big-clipboard-text-logo.jpg" 
                alt="Big Clipboard" 
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200">
                Home
              </Link>
              <a href="#features" className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200">
                Features
              </a>
              <a href="#faq" className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200">
                FAQ
              </a>
              <Link to="/downloads" className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200">
                Downloads
              </Link>
            </nav>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              <Link 
                to="/login" 
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200"
              >
                Sign in
              </Link>
              <Link 
                to="/register" 
                className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
              >
                Sign up
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden p-2 text-gray-700 hover:text-primary-600 transition-colors duration-200"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 bg-white">
              <div className="px-4 py-4 space-y-4">
                <Link 
                  to="/" 
                  className="block text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <a 
                  href="#features" 
                  className="block text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Features
                </a>
                <a 
                  href="#faq" 
                  className="block text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  FAQ
                </a>
                <Link 
                  to="/downloads" 
                  className="block text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Downloads
                </Link>
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <Link 
                    to="/login" 
                    className="block text-gray-700 hover:text-primary-600 font-medium transition-colors duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign in
                  </Link>
                  <Link 
                    to="/register" 
                    className="block bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200 text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign up
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section pt-32 lg:pt-36 md:pt-28">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto mb-16">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-8 leading-tight">
            Your Clipboard. Everywhere.
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8">
              Text, links, images, code — safe and synced across all your devices.
            </p>
            <p className="text-xl md:text-2xl text-gray-600 mb-8">
              Never lose what you copy.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link 
                to="/register" 
                className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-300 text-lg"
              >
                Get started for free
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm text-gray-500">
              <span>✓ Free to use</span>
              <span>✓ Works on all platforms</span>
            </div>
          </div>
          
          {/* Hero Image */}
          <div className="relative max-w-6xl mx-auto">
            <div className="hero-21-wrapper rounded-[34px] overflow-hidden border border-primary-100 shadow-xl bg-cover bg-center flex items-start justify-center pt-8 md:pt-12 lg:pt-20 h-[270px] md:h-[340px] lg:h-[475px]" style={{ backgroundImage: `url('/html-homepage/images/hero-21.jpg'), linear-gradient(to bottom right, #dbeafe, #ede9fe)` }}>
              <img 
                src="/html-homepage/images/tablet-01.png" 
                alt="Big Clipboard Dashboard" 
                className="w-auto max-w-3xl mx-auto block"
                style={{ zIndex: 2 }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Get Started Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How to Get Started</h2>
            <p className="text-lg text-gray-600">It only takes a minute to start using Big Clipboard</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Step 1 */}
            <div className="flex flex-col items-center bg-gray-50 rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-6 text-2xl font-bold text-primary-600">1</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Download & Install</h3>
              <p className="text-gray-600">
                <Link to="/downloads" className="text-primary-600 hover:underline font-medium">Download and install</Link> the small file on your PC or Mac.
              </p>
            </div>
            {/* Step 2 */}
            <div className="flex flex-col items-center bg-gray-50 rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-6 text-2xl font-bold text-primary-600">2</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Create an Account</h3>
              <p className="text-gray-600">Sign up for a free account on Big Clipboard to access your clipboard everywhere.</p>
            </div>
            {/* Step 3 */}
            <div className="flex flex-col items-center bg-gray-50 rounded-xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-6 text-2xl font-bold text-primary-600">3</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Enjoy!</h3>
              <p className="text-gray-600">Start copying, saving, and sharing content across all your devices instantly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      {false && (
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-4xl font-bold text-primary-600 mb-4">26.62k</h3>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Happy Users</h4>
              <p className="text-gray-600">Join thousands of users who trust Big Clipboard</p>
            </div>
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-4xl font-bold text-primary-600 mb-4">1.02M</h3>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Items Copied</h4>
              <p className="text-gray-600">Total items copied by all users</p>
            </div>
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-4xl font-bold text-primary-600 mb-4">13.54k</h3>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Items Shared</h4>
              <p className="text-gray-600">Copied items shared across the community</p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to manage your digital content efficiently
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-6">
                <Check className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Cross-Platform Sync</h3>
              <p className="text-gray-600">Access your clipboard items from any device, anywhere in the world.</p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Smart Organization</h3>
              <p className="text-gray-600">Automatically categorize and tag your content for easy discovery.</p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <Check className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Public Sharing</h3>
              <p className="text-gray-600">Share interesting content with the community and discover new things.</p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                <Check className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Advanced Search</h3>
              <p className="text-gray-600">Find exactly what you need with powerful search and filtering options.</p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                <Check className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Secure & Private</h3>
              <p className="text-gray-600">Your data is encrypted and private by default. You control what's shared.</p>
            </div>
            
            <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                <Check className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Real-time Updates</h3>
              <p className="text-gray-600">Changes sync instantly across all your devices in real-time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Integration Section */}
      {/* (Moved to Downloads page) */}

      {/* Reviews Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Users Probably Say
            </h2>
            <p className="text-xl text-gray-600">
              We think that they may say these things about us.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6">
                "Big Clipboard has completely changed how I organize my digital life. The cross-platform sync is seamless and the public sharing feature is brilliant!"
              </p>
              <div className="flex items-center">
                <UserCircle className="w-12 h-12 text-gray-400 rounded-full mr-4 bg-gray-100 p-2" />
                <div>
                  <h4 className="font-semibold text-gray-900">Sarah Johnson</h4>
                  <p className="text-gray-600">Product Manager</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6">
                "I love how easy it is to share interesting content with my team. The search functionality is incredibly powerful and saves me tons of time."
              </p>
              <div className="flex items-center">
                <UserCircle className="w-12 h-12 text-gray-400 rounded-full mr-4 bg-gray-100 p-2" />
                <div>
                  <h4 className="font-semibold text-gray-900">Mike Chen</h4>
                  <p className="text-gray-600">Software Developer</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6">
                "The security features give me peace of mind, and the interface is so intuitive. It's become an essential part of my daily workflow."
              </p>
              <div className="flex items-center">
                <UserCircle className="w-12 h-12 text-gray-400 rounded-full mr-4 bg-gray-100 p-2" />
                <div>
                  <h4 className="font-semibold text-gray-900">Emily Rodriguez</h4>
                  <p className="text-gray-600">Marketing Director</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about Big Clipboard
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg mb-4 shadow-sm">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                >
                  <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner Section */}
      <section className="py-20 bg-primary-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join thousands of users who are already organizing their digital life with Big Clipboard
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register" 
              className="bg-white hover:bg-gray-100 text-primary-600 font-semibold py-4 px-8 rounded-lg transition-colors duration-300 text-lg"
            >
              Start Free Account
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="inline-block mb-4">
                <img 
                  src="/big-clipboard-text-logo-white.png" 
                  alt="Big Clipboard" 
                  className="h-10 w-auto"
                />
              </Link>
              <p className="text-gray-400 mb-4 max-w-md">
                Your clipboard, everywhere. Sync text, links, images, and more across all your devices with Big Clipboard.
              </p>
              <div className="flex space-x-4">
                <span className="text-gray-400">✓ Free to use</span>
                <span className="text-gray-400">✓ Cross-platform</span>
                <span className="text-gray-400">✓ Secure</span>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/downloads" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Downloads
                  </Link>
                </li>
                <li>
                  <a href="#features" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#faq" className="text-gray-400 hover:text-white transition-colors duration-200">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Privacy Policy
                  </Link>
                </li>
                {/* <li>
                  <span className="text-gray-400 cursor-not-allowed">
                    Terms of Service
                  </span>
                </li>
                <li>
                  <span className="text-gray-400 cursor-not-allowed">
                    Cookie Policy
                  </span>
                </li> */}
              </ul>
              
              <h3 className="text-lg font-semibold mb-4 mt-8">Contact</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400">
              © {new Date().getFullYear()} Big Clipboard. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home; 